<template>
  <span
    ref="targetRef"
    class="tooltip__target"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false"
    @focus="isFocused = true"
    @blur="isFocused = false"
    @click="isFocused = false"
  >
    <slot />

    <Portal
      v-if="debouncedIsOpen && hasContent"
      :disabled="!usePortal"
      to="fullscreen"
    >
      <div
        ref="contentRef"
        :style="floatingStyles"
        class="tooltip__content"
        role="tooltip"
        @mouseover="isHovered = true"
        @mouseleave="isHovered = false"
      >
        <div
          v-if="content"
          class="tooltip__content-text"
        >
          {{ content }}
        </div>
        <slot
          name="content"
          content-class="tooltip__content-text"
        />

        <div
          ref="arrowRef"
          class="tooltip__arrow"
          :class="{
            'tooltip__arrow--left': placement.indexOf('right') === 0,
            'tooltip__arrow--right': placement.indexOf('left') === 0,
            'tooltip__arrow--top': placement.indexOf('bottom') === 0,
            'tooltip__arrow--bottom': placement.indexOf('top') === 0,
          }"
          :style="{
            left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : '',
            top: middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : '',
          }"
        >
          <slot name="arrow">
            <div class="tooltip__arrow-inner" />
          </slot>
        </div>
      </div>
    </Portal>
  </span>
</template>

<script lang="ts" setup>
import { computed, ref, useSlots } from 'vue'
import type { OffsetOptions, Placement } from '@floating-ui/vue'
import { shift } from '@floating-ui/vue'
import { offset as setOffset } from '@floating-ui/vue'
import { arrow, autoUpdate, flip, useFloating } from '@floating-ui/vue'
import { useDebounce } from '@vueuse/core'
import { Portal } from 'portal-vue'

const slots = useSlots()

const props = withDefaults(
  defineProps<{
    content?: string | null
    delay?: [number, number]
    offset?: OffsetOptions
    placement?: Placement
    autoShift?: boolean
    disabled?: boolean
    /**
     * We should avoid using portal unless necessary. 99% of tooltips can be rendered alongside the
     * target element, with some edge cases needing a different placement or an extra style.
     */
    usePortal?: boolean
  }>(),
  {
    content: null,
    placement: 'top',
    offset: 5,
    autoShift: false,
    delay: () => [300, 0],
    disabled: false,
    usePortal: false,
  },
)

const targetRef = ref<HTMLElement | null>(null)
const contentRef = ref<HTMLElement | null>(null)
const arrowRef = ref<HTMLElement | null>(null)

const middleware = [flip(), setOffset(props.offset)]

if (props.autoShift) {
  middleware.push(shift())
}

middleware.push(arrow({ element: arrowRef, padding: 5 }))

const { floatingStyles, middlewareData, placement } = useFloating(targetRef, contentRef, {
  placement: props.placement,
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware,
})

const isHovered = ref(false)
const isFocused = ref(false)
const isOpen = computed(() => isHovered.value || isFocused.value)
const hasContent = computed(() => !props.disabled && (!!props.content || !!slots.content))
const debouncedValue = computed(() => (isOpen.value ? props.delay[0] : props.delay[1]))
const debouncedIsOpen = useDebounce(isOpen, debouncedValue)
</script>

<!-- tooltips use portal, and we need to un-scope the styling -->
<!-- eslint-disable vue-scoped-css/enforce-style-type -->
<style lang="scss">
$arrow-size: 7px;
$default-max-width: 250px;

.tooltip__content {
  font-size: 11px;
  line-height: 16px;
  color: var(--color-text-inverted-irreversible);
  background: var(--color-surface-popover-inverted);
  border-radius: 4px;
  z-index: var(--z-index-tooltip);
  text-align: left;

  &-text {
    padding: 6px 8px;
    max-width: $default-max-width;
    font-weight: normal;
    white-space: normal;
    word-break: break-word;

    code {
      padding: 2px 4px;
      margin: 0 4px;
      border: 1px solid rgba(241, 245, 249, 0.3);
      border-radius: 5px;

      &:last-child {
        margin-right: 0;
      }
    }
  }

  .tooltip__arrow {
    position: absolute;
    left: 0;

    &-inner {
      background: var(--color-surface-popover-inverted);
      width: $arrow-size;
      height: $arrow-size;
      transform: rotate(45deg);
    }

    &.tooltip__arrow--left {
      left: calc(($arrow-size / 2) * -1);
    }
    &.tooltip__arrow--right {
      left: calc(100% - ($arrow-size / 2));
    }
    &.tooltip__arrow--top {
      top: calc(($arrow-size / 2) * -1);
    }
    &.tooltip__arrow--bottom {
      top: calc(100% - ($arrow-size / 2));
    }
  }
}

/**
 * These are `ToolTip component classes that can be used to apply specific styles to the content.
 * They work with default usage as well as with `use-portal` prop.
 * Only the first class is needed. Ie. `tooltip__content--nowrap` is enough.
 */
.tooltip__content--break-word .tooltip__content-text {
  word-break: break-word;
}
.tooltip__content--nowrap .tooltip__content-text {
  white-space: nowrap;
}
</style>
